// Read-only admin audit of every member's Two-Step Acceptance state (§15/§34).
// Admins cannot mutate acceptance rows from this UI — the backend has no
// endpoint to do so.
import { useEffect, useState } from "react";
import api from "@/modules/growing-circle/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ accepted }) {
  return accepted ? (
    <span className="inline-flex items-center gap-1 text-[0.7rem] uppercase tracking-[0.12em] text-primary">
      <CheckCircle2 className="w-3.5 h-3.5" /> Accepted
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[0.7rem] uppercase tracking-[0.12em] text-destructive">
      <XCircle className="w-3.5 h-3.5" /> Pending
    </span>
  );
}

export default function AdminAcceptances() {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/acceptances")
      .then(({ data }) => setPayload(data))
      .catch((e) => setError(e?.response?.data?.detail || "load_error"));
  }, []);

  if (error) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 text-destructive text-sm" data-testid="admin-acceptances-error">
        Could not load acceptances.
      </div>
    );
  }
  if (!payload) {
    return <p className="text-muted-foreground text-sm" data-testid="admin-acceptances-loading">Loading…</p>;
  }

  const current = payload.current_versions || {};
  return (
    <div data-testid="admin-acceptances">
      <h1 className="font-heading text-3xl tracking-tight mb-2">Terms Acceptances</h1>
      <p className="text-muted-foreground text-sm mb-6">
        Read-only audit. Current versions:
        {" "}
        <span className="text-foreground">Step 1 {current.protected_content_terms}</span>
        {" · "}
        <span className="text-foreground">Step 2 {current.community_privacy_terms}</span>
      </p>
      <div className="overflow-x-auto bg-card border border-border rounded-2xl">
        <table className="min-w-full text-sm" data-testid="admin-acceptances-table">
          <thead className="text-xs uppercase tracking-[0.12em] text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">Member</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Step 1</th>
              <th className="text-left px-4 py-3">Step 1 · Version</th>
              <th className="text-left px-4 py-3">Step 1 · Date</th>
              <th className="text-left px-4 py-3">Step 2</th>
              <th className="text-left px-4 py-3">Step 2 · Version</th>
              <th className="text-left px-4 py-3">Step 2 · Date</th>
            </tr>
          </thead>
          <tbody>
            {payload.items.map((it) => {
              const s1 = it.protected_content_terms || {};
              const s2 = it.community_privacy_terms || {};
              return (
                <tr key={it.user_id} className="border-t border-border" data-testid={`acceptance-row-${it.user_id}`}>
                  <td className="px-4 py-3">
                    <span className="font-medium">{it.name || "—"}</span>
                    <span className="ml-2 text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground">{it.role}</span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{it.email}</td>
                  <td className="px-4 py-3"><StatusBadge accepted={!!s1.accepted} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{s1.accepted_version || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(s1.accepted_at)}</td>
                  <td className="px-4 py-3"><StatusBadge accepted={!!s2.accepted} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{s2.accepted_version || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(s2.accepted_at)}</td>
                </tr>
              );
            })}
            {payload.items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">No members yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
